;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { uniq } from "lodash-es";
import LZString from "lz-string";
import qs from "qs";
import type { StateStorage } from "zustand/middleware";
import { logger } from "@/lib/services/logger";
import {
	stringifyTopLevelFilters,
	TOP_LEVEL_FILTERS_URL_PARAM,
} from "@/lib/stores/top-level-filter-query";

export { TOP_LEVEL_FILTERS_URL_PARAM };

const isServer = typeof window === "undefined";

type UrlStorageOptions = {
	/** The URL parameter name to use for storing state */
	paramName: string;
	/** Whether to debounce URL updates (in ms). Default: 0 (no debounce) */
	debounceMs?: number;
};

// QS parse options for consistent decoding
const QS_PARSE_OPTIONS: qs.IParseOptions = {
	allowDots: true,
	arrayLimit: 200,
	delimiter: ":",
	duplicates: "combine",
	parseArrays: true,
	plainObjects: true,
};

/**
 * Coerce parsed string values to their proper types (numbers, arrays).
 * QS parses everything as strings, so we need to convert back.
 */
const coerceTypes = (parsed: Record<string, any>): Record<string, any> => {
	const result: Record<string, any> = {};

	for (const [key, value] of Object.entries(parsed)) {
		if (Array.isArray(value)) {
			// Convert array items to numbers if possible, and remove duplicates
			result[key] = uniq(value).map((item) => {
				const numberValue = Number(item);
				return Number.isNaN(numberValue) ? item : numberValue;
			});
		} else if (typeof value === "string") {
			// Convert string to number if possible
			const numberValue = Number(value);
			result[key] = Number.isNaN(numberValue) ? value : numberValue;
		} else {
			result[key] = value;
		}
	}

	return result;
};

export function serializeTopLevelFiltersForUrl(
	filters: Record<string, unknown>,
): string {
	return LZString.compressToEncodedURIComponent(
		stringifyTopLevelFilters(filters),
	);
}

export function parseTopLevelFiltersFromUrlParam(
	compressedValue: string,
): Record<string, unknown> {
	let filterString = compressedValue;
	try {
		const decompressed =
			LZString.decompressFromEncodedURIComponent(compressedValue);
		if (decompressed) {
			filterString = decompressed;
		}
	} catch {
		// Uncompressed legacy URLs
	}

	return coerceTypes(
		qs.parse(filterString, QS_PARSE_OPTIONS) as Record<string, unknown>,
	) as Record<string, unknown>;
}

/**
 * Read top-level filters from the current URL synchronously, using the same decode path as
 * Zustand URL persist. Use when the persist store has not rehydrated yet (e.g. `useLayoutEffect`
 * before async rehydration), so chart state can match the URL on the first request.
 */
export function readTopLevelFiltersFromUrl(): Record<string, unknown> {
	if (isServer) {
		return {};
	}
	try {
		const searchParams = new URLSearchParams(window.location.search);
		const compressedValue = searchParams.get(TOP_LEVEL_FILTERS_URL_PARAM);
		if (!compressedValue) {
			return {};
		}

		return parseTopLevelFiltersFromUrlParam(compressedValue);
	} catch (error) {
		logger.error(
			"[urlStorage] Error reading filters from URL",
			error instanceof Error ? error : new Error(String(error)),
			{ feature: "url-storage" },
		);
		return {};
	}
}

/**
 * Creates a custom Zustand StateStorage that persists state to URL search params.
 * Uses qs for serialization and LZString for compression to keep URLs manageable.
 *
 * @example
 * ```ts
 * const useStore = create(
 *   persist(
 *     (set) => ({ filters: {}, setFilter: (key, value) => set(...) }),
 *     {
 *       name: 'filters',
 *       storage: createJSONStorage(() => createUrlStorage({ paramName: 'filters' })),
 *     }
 *   )
 * );
 * ```
 */
export const createUrlStorage = ({
	paramName,
	debounceMs = 0,
}: UrlStorageOptions): StateStorage => {
	// Server-side: return no-op storage
	if (isServer) {
		return {
			getItem: () => null,
			removeItem: () => {},
			setItem: () => {},
		};
	}

	let debounceTimer: ReturnType<typeof setTimeout> | null = null;

	const getSearchParams = () => {
		return new URLSearchParams(window.location.search);
	};

	const updateUrl = (searchParams: URLSearchParams) => {
		const newUrl = `${window.location.pathname}${
			searchParams.toString() ? `?${searchParams.toString()}` : ""
		}${window.location.hash}`;

		window.history.replaceState(window.history.state, "", newUrl);
	};

	return {
		getItem: (_key: string): string | null => {
			try {
				const searchParams = getSearchParams();
				const compressedValue = searchParams.get(paramName);

				if (!compressedValue) {
					return null;
				}

				// Try to decompress first
				let filterString = compressedValue;
				try {
					const decompressed =
						LZString.decompressFromEncodedURIComponent(compressedValue);
					if (decompressed) {
						filterString = decompressed;
					}
				} catch (_e) {
					// If decompression fails, assume it's uncompressed (backward compatibility)
				}

				// Parse with qs
				const parsedFilters = qs.parse(filterString, QS_PARSE_OPTIONS);

				// Coerce types (strings to numbers where appropriate)
				const typedFilters = coerceTypes(parsedFilters);

				// Return in Zustand persist format
				return JSON.stringify({
					state: { filters: typedFilters },
					version: 0,
				});
			} catch (error) {
				logger.error(
					"[urlStorage] Error reading from URL",
					error instanceof Error ? error : new Error(String(error)),
					{ feature: "url-storage" },
				);
				return null;
			}
		},

		removeItem: (_key: string): void => {
			try {
				if (debounceTimer) {
					clearTimeout(debounceTimer);
				}
				const searchParams = getSearchParams();
				searchParams.delete(paramName);
				updateUrl(searchParams);
			} catch (error) {
				logger.error(
					"[urlStorage] Error removing from URL",
					error instanceof Error ? error : new Error(String(error)),
					{ feature: "url-storage" },
				);
			}
		},

		setItem: (_key: string, value: string): void => {
			try {
				const doUpdate = () => {
					const searchParams = getSearchParams();

					// Parse the Zustand persist format
					const parsed = JSON.parse(value);
					const filters = parsed.state?.filters || {};
					const isEmpty = Object.keys(filters).length === 0;

					if (isEmpty) {
						searchParams.delete(paramName);
					} else {
						searchParams.set(
							paramName,
							serializeTopLevelFiltersForUrl(filters),
						);
					}

					updateUrl(searchParams);
				};

				if (debounceMs > 0) {
					if (debounceTimer) {
						clearTimeout(debounceTimer);
					}
					debounceTimer = setTimeout(doUpdate, debounceMs);
				} else {
					doUpdate();
				}
			} catch (error) {
				logger.error(
					"[urlStorage] Error writing to URL",
					error instanceof Error ? error : new Error(String(error)),
					{ feature: "url-storage" },
				);
			}
		},
	};
};
